export interface WorkExperience {
  id: string;
  company: string;
  title: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
}

export interface Education {
  id: string;
  institution: string;
  degree: string;
  field: string;
  graduationYear: string;
}

export interface UserProfile {
  personal: {
    fullName: string;
    email: string;
    phone: string;
    location: string;
    // Address breakdown
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    // Extended fields
    nationality: string;
    dateOfBirth: string;
    gender: string;
    workAuthorization: string;
    yearsOfExperience: string;
    seniorityLevel: string;
    timeZone: string;
    linkedIn: string;
    portfolio: string;
    github: string;
  };
  summary: string;
  experience: WorkExperience[];
  education: Education[];
  skills: string[];
  commonAnswers: Record<string, string>;
}

export interface Application {
  id: string;
  company: string;
  position: string;
  date: string;
  status: 'applied' | 'interviewing' | 'rejected' | 'offered';
  url?: string;
  ctc?: string;
  showCtc?: boolean;
  workMode?: 'onsite' | 'hybrid' | 'remote';
}

export interface SavedQuestion {
  id: string;
  question: string;
  answer: string;
  starred: boolean;
  jobDescription?: string;
}

export type Currency = 'USD' | 'EUR' | 'GBP' | 'INR' | 'CAD' | 'AUD';

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'C$', AUD: 'A$',
};

export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  currency: Currency;
  subscription: {
    status: 'trial' | 'active' | 'expired';
    trialStart: string;
    expiresAt: string;
  };
}

export interface AppState {
  profile: UserProfile;
  applications: Application[];
  savedQuestions: SavedQuestion[];
  settings: AppSettings;
  onboarded: boolean;
}

const DEFAULT_STATE: AppState = {
  profile: {
    personal: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: '',
      postcode: '',
      nationality: '',
      dateOfBirth: '',
      gender: '',
      workAuthorization: '',
      yearsOfExperience: '',
      seniorityLevel: '',
      timeZone: '',
      linkedIn: '',
      portfolio: '',
      github: '',
    },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    commonAnswers: {},
  },
  savedQuestions: [],
  applications: [],
  settings: {
    theme: 'system',
    currency: 'USD' as Currency,
    subscription: {
      status: 'trial',
      trialStart: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    },
  },
  onboarded: false,
};

const STORAGE_KEY = 'jobily_data';

export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Merge with defaults to handle new fields added in updates
      return {
        ...DEFAULT_STATE,
        ...parsed,
        profile: {
          ...DEFAULT_STATE.profile,
          ...parsed.profile,
          personal: {
            ...DEFAULT_STATE.profile.personal,
            ...parsed.profile?.personal,
          },
        },
        settings: {
          ...DEFAULT_STATE.settings,
          ...parsed.settings,
        },
      };
    }
  } catch {}
  return { ...DEFAULT_STATE };
}

export function saveState(state: AppState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}